import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, formatDate } from '../lib/api';
import { PageHeader, EmptyState, MiniStat } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Megaphone, Search, Download, Mail, Phone, Loader2, Users, CheckCircle2, Percent, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { useNavigate } from 'react-router';
import { normalizaTexto, telefonoConPrefijo, telefonoParaEnlace } from '../lib/utils';

export default function MarketingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Campañas por correo (2026-07-26): se mandan desde los mismos buzones
  // de la bandeja Mail, uno a uno y con enlace de baja.
  const [cuentas, setCuentas] = useState([]);
  const [campanas, setCampanas] = useState([]);
  const [elegidos, setElegidos] = useState([]);   // ids marcados en la tabla
  const [campanaOpen, setCampanaOpen] = useState(false);
  const [cuenta, setCuenta] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [enviando, setEnviando] = useState(false);
  // Vista previa del correo cuando la prueba sale en simulación (demo).
  const [previa, setPrevia] = useState(null);

  useEffect(() => { if (user && user.role !== 'admin') nav('/'); }, [user, nav]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c, d] = await Promise.all([
        api.get('/api/marketing/consented'),
        api.get('/api/marketing/stats'),
        api.get('/api/marketing/cuentas'),
        api.get('/api/marketing/campanas'),
      ]);
      setItems(a.data.items);
      setStats(b.data);
      setCuentas(c.data.items);
      setCampanas(d.data.items);
      // En la demo ningún buzón tiene contraseña, así que se elige el primero
      // igualmente: el envío sale en modo simulación.
      setCuenta((prev) => prev || c.data.items.find((x) => x.configurada)?.clave
        || c.data.items[0]?.clave || '');
    } catch (e) {
      toast.error('No se pudo cargar el panel de marketing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Mientras una campaña esté enviando, refresca el avance cada 5 s.
  const enviandoAhora = campanas.some((c) => c.estado === 'enviando');
  useEffect(() => {
    if (!enviandoAhora) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.get('/api/marketing/campanas');
        setCampanas(r.data.items);
      } catch { /* si falla una vuelta, se reintenta en la siguiente */ }
    }, 5000);
    return () => clearInterval(t);
  }, [enviandoAhora]);

  const filtered = useMemo(() => {
    const q = normalizaTexto(search.trim());
    if (!q) return items;
    return items.filter(it =>
      normalizaTexto(it.full_name).includes(q)
      || normalizaTexto(it.email).includes(q)
      || (it.phone || '').includes(q)
      || normalizaTexto(it.nif).includes(q),
    );
  }, [items, search]);

  const exportCsv = () => {
    if (!filtered.length) return toast.info('Nada que exportar');
    const headers = ['Nombre', 'Email', 'Teléfono', 'NIF', 'Ciudad', 'País', 'Fecha consentimiento'];
    const rows = filtered.map(it => [
      it.full_name, it.email, it.phone, it.nif, it.city, it.country, it.consented_at || '',
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marketing_consented_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyAllEmails = async () => {
    const emails = filtered.filter(i => i.email).map(i => i.email).join(', ');
    if (!emails) return toast.info('No hay emails');
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`Copiados ${filtered.filter(i => i.email).length} emails`);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  // Solo se puede escribir a quien tiene email. La cabecera marca/desmarca los
  // que se están viendo, que es lo que espera quien acaba de filtrar.
  const conEmail = useMemo(() => filtered.filter((i) => i.email), [filtered]);
  const marcados = useMemo(
    () => conEmail.filter((i) => elegidos.includes(i.id)).length,
    [conEmail, elegidos],
  );
  const todosMarcados = conEmail.length > 0 && marcados === conEmail.length;
  // Buzón sin contraseña = envío simulado (es lo normal en esta demo).
  const cuentaSel = cuentas.find((c) => c.clave === cuenta);
  const simulado = !!cuentaSel && !cuentaSel.configurada;

  const marcarTodos = (v) => {
    const ids = conEmail.map((i) => i.id);
    setElegidos((prev) => (v ? [...new Set([...prev, ...ids])] : prev.filter((x) => !ids.includes(x))));
  };

  const toggleUno = (id) => setElegidos(
    (prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]),
  );

  const abrirCampana = () => {
    if (!marcados) return toast.info('Marca primero a quién se lo mandas');
    setConfirmando(false);
    setCampanaOpen(true);
    return undefined;
  };

  const enviarPrueba = async () => {
    if (!asunto.trim() || !cuerpo.trim()) return toast.info('Escribe el asunto y el mensaje');
    const destino = window.prompt('¿A qué dirección mando la prueba?', user?.email || '');
    if (!destino) return undefined;
    setEnviando(true);
    try {
      const r = await api.post('/api/marketing/campanas', {
        cuenta, asunto, cuerpo, destinatarios: [], prueba_email: destino,
      });
      if (r.data?.simulada) {
        setPrevia({ destino, ...r.data.vista_previa });
      } else {
        toast.success(`Prueba enviada a ${destino}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar la prueba');
    } finally {
      setEnviando(false);
    }
    return undefined;
  };

  const enviarCampana = async () => {
    setEnviando(true);
    try {
      const r = await api.post('/api/marketing/campanas', {
        cuenta, asunto, cuerpo,
        destinatarios: conEmail.filter((i) => elegidos.includes(i.id)).map((i) => i.id),
      });
      const fuera = r.data.descartados;
      toast.success(
        `${r.data.campana.simulada ? 'Campaña simulada' : 'Campaña'} en marcha: ${r.data.campana.total} correos`
        + (fuera ? ` (${fuera} descartados por baja o email no válido)` : ''),
      );
      setCampanaOpen(false);
      setConfirmando(false);
      setAsunto('');
      setCuerpo('');
      setElegidos([]);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo lanzar la campaña');
    } finally {
      setEnviando(false);
    }
  };

  const copyAllPhones = async () => {
    const phones = filtered.filter(i => i.phone).map(i => i.phone).join(', ');
    if (!phones) return toast.info('No hay teléfonos');
    try {
      await navigator.clipboard.writeText(phones);
      toast.success(`Copiados ${filtered.filter(i => i.phone).length} teléfonos`);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Marketing"
        subtitle="Pacientes que han consentido recibir información comercial (promociones, novedades)."
        actions={(
          <div className="flex gap-2">
            <Button
              onClick={abrirCampana}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="marketing-nueva-campana"
            >
              <Send className="w-4 h-4 mr-1" /> Enviar campaña{marcados ? ` (${marcados})` : ''}
            </Button>
            <Button variant="outline" onClick={copyAllEmails} data-testid="marketing-copy-emails">
              <Mail className="w-4 h-4 mr-1" /> Copiar emails
            </Button>
            <Button variant="outline" onClick={copyAllPhones} data-testid="marketing-copy-phones">
              <Phone className="w-4 h-4 mr-1" /> Copiar teléfonos
            </Button>
            <Button onClick={exportCsv} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="marketing-export-csv">
              <Download className="w-4 h-4 mr-1" /> Exportar CSV
            </Button>
          </div>
        )}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MiniStat label="Pacientes totales" value={stats?.total_patients ?? '—'} icon={Users} />
        <MiniStat label="Han aceptado comercial" value={stats?.consented_commercial ?? '—'} icon={CheckCircle2} hint={`${filtered.length} mostrados`} />
        <MiniStat label="Tasa de aceptación" value={stats ? `${stats.rate}%` : '—'} icon={Percent} />
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, email, teléfono…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="marketing-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="Sin pacientes con consentimiento comercial"
          description="A medida que los pacientes firmen la LOPD en la tablet y marquen la casilla opcional de promociones, aparecerán aquí."
        />
      ) : (
        <div className="cp-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/50">
              <tr>
                <th className="py-2.5 px-3 w-10">
                  <Checkbox
                    checked={todosMarcados}
                    onCheckedChange={marcarTodos}
                    aria-label="Marcar todos los que se están viendo"
                    data-testid="marketing-check-todos"
                  />
                </th>
                <th className="py-2.5 px-3">Paciente</th>
                <th className="py-2.5 px-3">Email</th>
                <th className="py-2.5 px-3">Teléfono</th>
                <th className="py-2.5 px-3">Ciudad / País</th>
                <th className="py-2.5 px-3">Aceptado</th>
                <th className="py-2.5 px-3 text-right">Ficha</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-t border-border hover:bg-muted/30" data-testid="marketing-row">
                  <td className="py-2.5 px-3">
                    <Checkbox
                      checked={elegidos.includes(it.id)}
                      onCheckedChange={() => toggleUno(it.id)}
                      disabled={!it.email}
                      aria-label={`Incluir a ${it.full_name} en la campaña`}
                      title={it.email ? '' : 'Sin email: no se le puede escribir'}
                      data-testid="marketing-check-uno"
                    />
                  </td>
                  <td className="py-2.5 px-3 font-medium text-[#0B1220]">
                    {it.full_name}
                    <div className="text-[11px] text-muted-foreground">{it.nif}</div>
                  </td>
                  <td className="py-2.5 px-3 text-xs">
                    {it.email
                      ? <a href={`mailto:${it.email}`} className="text-[#13231E] underline">{it.email}</a>
                      : <span className="text-muted-foreground italic">—</span>}
                  </td>
                  <td className="py-2.5 px-3 text-xs whitespace-nowrap">
                    {it.phone
                      ? <a href={`tel:${telefonoParaEnlace(it.phone)}`} className="text-[#13231E] underline">{telefonoConPrefijo(it.phone)}</a>
                      : <span className="text-muted-foreground italic">—</span>}
                  </td>
                  <td className="py-2.5 px-3 text-xs text-muted-foreground">{it.city}{it.country ? ` · ${it.country}` : ''}</td>
                  <td className="py-2.5 px-3 text-xs">
                    <Badge variant="secondary" className="bg-green-50 text-green-800 border-green-300">
                      {formatDate(it.consented_at) || '—'}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => nav(`/pacientes/${it.id}`)} data-testid="marketing-row-view">
                      Ver ficha
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Historial: qué se mandó, desde qué buzón y cómo fue. */}
      {campanas.length > 0 && (
        <div className="cp-card p-4 space-y-2" data-testid="marketing-campanas">
          <div className="text-sm font-semibold text-[#13231E]">Campañas enviadas</div>
          {campanas.map((c) => (
            <div key={c.id} className="flex items-center gap-3 text-xs border-t border-border pt-2">
              <Badge variant="secondary" className="shrink-0">{c.cuenta_label}</Badge>
              {c.simulada && (
                <Badge variant="secondary" className="shrink-0 bg-amber-50 text-amber-800 border-amber-300">
                  Simulada
                </Badge>
              )}
              <span className="font-medium text-[#0B1220] truncate">{c.asunto}</span>
              <span className="text-muted-foreground shrink-0 ml-auto">
                {formatDate(c.creada_at)} · {c.creada_por}
              </span>
              <span className="shrink-0">
                {c.estado === 'enviando'
                  ? <span className="text-amber-700 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> {c.enviados}/{c.total}
                    </span>
                  : <span className="text-green-800">
                      {c.enviados}/{c.total} enviados{c.fallidos ? ` · ${c.fallidos} fallidos` : ''}
                    </span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Redactar y mandar la campaña */}
      <Dialog open={campanaOpen} onOpenChange={(v) => { setCampanaOpen(v); if (!v) setConfirmando(false); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Enviar campaña a {marcados} paciente{marcados === 1 ? '' : 's'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                Se envía desde
              </div>
              <div className="flex flex-wrap gap-2">
                {cuentas.map((c) => (
                  <button
                    key={c.clave}
                    type="button"
                    onClick={() => setCuenta(c.clave)}
                    className={`px-3 py-2 rounded-md border text-left text-xs transition ${
                      cuenta === c.clave
                        ? 'border-[#2E6E5E] bg-[rgba(46,110,94,0.08)]'
                        : 'border-border hover:bg-muted/50'
                    }`}
                    data-testid={`marketing-cuenta-${c.clave}`}
                  >
                    <div className="font-semibold text-[#13231E]">{c.label}</div>
                    <div className="text-muted-foreground">{c.email}</div>
                    {!c.configurada && <div className="text-amber-700">Modo demostración</div>}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Asunto</div>
              <Input
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                placeholder="Ej.: Novedades de primavera en la clínica"
                data-testid="marketing-campana-asunto"
              />
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Mensaje</div>
              <Textarea
                value={cuerpo}
                onChange={(e) => setCuerpo(e.target.value)}
                rows={9}
                placeholder={'Hola {nombre},\n\n…'}
                data-testid="marketing-campana-cuerpo"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Escribe <code>{'{nombre}'}</code> donde quieras que salga el nombre de cada
                paciente. La firma del buzón y el enlace de baja se añaden solos.
              </p>
            </div>

            <div className="flex items-start gap-2 text-[11px] text-muted-foreground border border-border rounded-md p-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-px" />
              <span>
                Cada paciente recibe su propio correo — nadie ve la dirección de los
                demás. Va uno por segundo, así que {marcados} correos tardan unos{' '}
                {Math.max(1, Math.round(marcados / 60))} minuto{marcados > 60 ? 's' : ''} en salir.
                Quien se dé de baja desaparece solo de las siguientes campañas.
                {simulado && (
                  <> <strong className="text-amber-800">En esta demo el buzón no está
                  conectado: la campaña recorre todo el proceso sin llegar a mandar
                  ningún correo real.</strong></>
                )}
              </span>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={enviarPrueba} disabled={enviando} data-testid="marketing-campana-prueba">
              Mandarme una prueba
            </Button>
            {confirmando ? (
              <Button
                onClick={enviarCampana}
                disabled={enviando}
                className="bg-red-700 hover:bg-red-800"
                data-testid="marketing-campana-confirmar"
              >
                {enviando
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Lanzando…</>
                  : `Sí, enviar a ${marcados} pacientes`}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  if (!cuenta) return toast.info('Elige el buzón desde el que se manda');
                  if (!asunto.trim() || !cuerpo.trim()) return toast.info('Falta el asunto o el mensaje');
                  setConfirmando(true);
                  return undefined;
                }}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                data-testid="marketing-campana-enviar"
              >
                <Send className="w-4 h-4 mr-1" /> Enviar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vista previa de la prueba en modo demostración: el correo tal cual
          saldría, sin salir de la app. */}
      <Dialog open={!!previa} onOpenChange={(v) => { if (!v) setPrevia(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Así saldría el correo</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
              Modo demostración: no se ha enviado nada. Con el buzón conectado, este
              mismo correo saldría hacia {previa?.destino}.
            </p>
            <div className="text-xs text-muted-foreground">De: {previa?.de}</div>
            <div className="font-medium text-[#13231E]">{previa?.asunto}</div>
            <pre className="whitespace-pre-wrap font-sans text-xs border border-border rounded-md p-3 bg-muted/30 max-h-80 overflow-y-auto">
              {previa?.cuerpo}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
